/**
 * Mobile (below lg) layout of the future-dates modal: a full-width slide-in panel with the
 * selected lines' ship dates and quantities, then a date picker for `rows[0]` with Apply and
 * "Remove Future Date". State and handlers come from `FutureDatesModal.tsx`; the desktop
 * counterpart is `FutureDatesDesktop.tsx`, and a change to what is shown belongs in both.
 *
 * The panel opens and closes by toggling the `slide-open` / `slide-close` classes of
 * `Helper/commonStyles.tsx` → `SliderMenu`, and locks page scrolling while open (see the effect
 * below). `Shared/MultipleDatesPicker/MultipleDatesPickerMobile.tsx` uses the same mechanism.
 */
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import React, { RefObject, createRef, useEffect, useState } from "react";
import { Icon } from "@ryerson/frontend.assets";
import { FutureDatesType } from "./FutureDatesModal";
import { SliderMenu, datePickerStyle } from "@components/Helper/commonStyles";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@ryerson/frontend.table";
import styled from "@emotion/styled";
import { IndexedLayers } from "@ryerson/frontend.common";
import rgba from "polished/lib/color/rgba";
import { getDisplayUom, useApplication } from "@ryerson/frontend.application";
import { FutureDatesContent } from "./FutureDatesContent";
import { getShipDates } from "@components/Helper/DeliveryDateAndMinMaxRange";
import { Alert } from "@ryerson/frontend.notification";
import { DatePickerPopper } from "../DatePickerPopper/DatePickerPopper";
import { Button } from "@ryerson/frontend.button";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { isShoppingCartDataType } from "@components/Helper/TypeHelpers";
import { getLeadTimeAndDeliveryDates } from "./utils";

const QuantityCell = styled(TableCell)``;

const DateCell = styled(TableCell)`
	width: 50%;
`;

/** Dark 50px header bar of the slide-in panel (back and close controls). */
export const SliderHeader = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.lightGray};
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

const closeFiltersPane = (props: any) => {
	const { theme } = props;
	return css`
		height: 50px;
		width: 50px;
		border-left: 1px solid ${rgba(theme.colors.primary.white, 0.1)};
	`;
};

/**
 * Renders the slide-in panel. Back, the chevron and the close icon all call
 * `setModalOpen(false)`; the panel calls `onClose` itself when it slides shut.
 */
export const FutureDatesMobile: React.FC<FutureDatesType> = ({
	filteredDeliveryTimes,
	handleApply,
	handleChange,
	isFetching,
	modalOpen,
	onClose,
	productsDeliveryTimes,
	rows,
	setModalOpen,
	showError,
	errorMessage,
	handleRemove,
	popperDisplayedState,
	triggerRef,
	titleOverride,
}) => {
	const { flexibleDelivery } = useEcommerce();
	const { theme } = useTheme();
	const { white, lighterGray, gray, text, darkGray, link } = theme.colors.primary;

	const { isNative } = useApplication();
	const {
		localization: { language },
	} = useApplication();
	const content = FutureDatesContent[language || "en"];

	const sliderRef = createRef<HTMLDivElement>();
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const [hasMounted, setHasMounted] = useState<boolean>(false);

	// Each change of modalOpen after mount flips the panel between open and closed, so the
	// classes stay in step only while modalOpen alternates. Opening scrolls to the top, saves the
	// body's inline style and sets overflow hidden after 100 ms; closing restores the saved style.
	useEffect(() => {
		if (hasMounted) {
			const body = document?.querySelector("body");
			const sliderEl = sliderRef.current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			if (sliderEl?.classList.contains("slide-close")) {
				onClose();
			}

			if (modalOpen) {
				window.scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 100);
			} else {
				body && body.setAttribute("style", bodyStyles);
			}
		}
	}, [modalOpen]);

	useEffect(() => {
		setHasMounted(true);
		const body = document?.querySelector("body");

		body && body.setAttribute("style", bodyStyles);
	}, []);

	const leadTimeAndDeliveryDates =
		rows && getLeadTimeAndDeliveryDates(rows[0], productsDeliveryTimes);

	const hasMultipleDeliveryDates =
		leadTimeAndDeliveryDates && leadTimeAndDeliveryDates.leadTimeDays?.length > 1;

	return (
		<>
			<SliderMenu
				{...{ theme: theme }}
				ref={sliderRef}
				className={`future-dates-menu slide-close`}
				css={{
					top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 50,
				}}
			>
				<SliderHeader theme={theme}>
					<Flex alignItems="center">
						<FlexItem grow={1}>
							<Flex justifyContent="flex-start">
								<FlexItem
									alignSelf="center"
									css={{ marginLeft: 20 }}
									onClick={() => setModalOpen(false)}
								>
									<Icon
										icon="chevron-left"
										size="md"
										onClick={() => setModalOpen(false)}
										circled={true}
										color={white}
										css={{ borderColor: white }}
									/>
								</FlexItem>
								<FlexItem
									css={css`
										padding-left: 10px;
									`}
									onClick={() => setModalOpen(false)}
								>
									<Typography
										variant="p"
										color={theme.colors.tertiary.label}
										size="md"
									>
										{content.back}
									</Typography>
								</FlexItem>
							</Flex>
						</FlexItem>
						<FlexItem>
							<Flex
								style={closeFiltersPane({ theme: theme })}
								justifyContent="center"
								alignItems="center"
							>
								<Icon
									icon="close"
									size="sm"
									onClick={() => setModalOpen(false)}
									color={white}
								/>
							</Flex>
						</FlexItem>
					</Flex>
				</SliderHeader>

				<Flex direction="column" css={{ gap: 10, padding: 20 }}>
					<Typography size="xl" css={{ marginBottom: 14 }}>
						{titleOverride ??
							(rows?.[0]?.canSelectFutureDeliveryDateForTBDItems &&
							!hasMultipleDeliveryDates
								? content.deliveryDate
								: content.multipleDates)}
					</Typography>
					<Typography weight="bold" size="lg" css={{ marginBottom: 14 }}>
						{content.availableToShip}
					</Typography>
					{hasMultipleDeliveryDates && (
						<Typography css={{ marginBottom: 28 }}>
							{content.multipleShipments}
						</Typography>
					)}
					{rows?.[0]?.canSelectFutureDeliveryDateForTBDItems && (
						<Flex css={{ gap: "10px" }}>
							<Icon icon={"warning"} size="md" />
							<Typography
								css={{ marginBottom: 40, width: "fit-content" }}
								color={darkGray}
							>
								{content.customerPartDeliveryDateMessage}
							</Typography>
						</Flex>
					)}
					<Table
						addStyleString={`
									display:block; 
									width: 100%;
									border-collapse: collapse;
									border-spacing: 0px;
									position: relative;
									overflow-y: auto;
									overflow-x: unset;
									max-height: 300px;
									border: solid ${lighterGray} 1px;
								`}
					>
						<TableHead
							css={css`
								display: table;
								width: 100%;
								table-layout: fixed;
								top: 0;
								position: sticky;
								z-index: ${IndexedLayers.base};
								height: 64px;
							`}
						>
							<TableRow
								css={css`
									background: ${gray};
									height: 60px;
								`}
							>
								<QuantityCell>
									<Typography
										css={{
											color: white,
											background: gray,
											padding: 10,
										}}
									>
										{content.quantity}
									</Typography>
								</QuantityCell>
								<DateCell>
									<Typography
										css={{
											color: white,
											background: gray,
											padding: 10,
										}}
									>
										{content.date}
									</Typography>
								</DateCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<>
								{rows
									.flatMap((rowData, rowDataIndex: number) => {
										const leadTimeAndDeliveryDates = productsDeliveryTimes.find(
											(product) =>
												product.materialSku ===
													(isShoppingCartDataType(rowData)
														? rowData.materialMaster
														: rowData.skuNumber) &&
												product.itemNumber === rowData.itemNumber
										);
										return getShipDates(
											leadTimeAndDeliveryDates,
											flexibleDelivery
										).map((shipDate) => {
											return {
												...shipDate,
												quantityUOM: isShoppingCartDataType(rowData)
													? rowData.quantityUOM
													: rowData.quantityUom,
												rowDataIndex,
											};
										});
									})
									.map((shipDate, index: number) => {
										const {
											deliveryDate,
											deliveryQuantity: ShipDateQuantity,
											rowDataIndex,
										} = shipDate || {};
										const displayDate = deliveryDate || content.tbd;
										/* deliveryQuantity - For normal parts and multiple dates customer TBD part deliveryTimes call returns deliveryQuantity, 
											but for single customer TBD part deliveryTimes call returns no deliveryQuantity, for that we are displaying row quantity
										*/
										const deliveryQuantity =
											rows[rowDataIndex]
												.canSelectFutureDeliveryDateForTBDItems &&
											!ShipDateQuantity
												? rows[rowDataIndex].quantity
												: shipDate.deliveryQuantity;

										return (
											<TableRow
												key={index}
												css={css`
													display: table;
													width: 100%;
													height: 60px;
													table-layout: fixed;
												`}
											>
												<QuantityCell>
													<FlexItem
														css={{
															padding: 10,
														}}
													>
														<Typography color={text}>
															{deliveryQuantity
																? `${deliveryQuantity} ${getDisplayUom(
																		shipDate.quantityUOM,
																		"quantity"
																	)}`
																: ""}
														</Typography>
													</FlexItem>
												</QuantityCell>
												<DateCell>
													<FlexItem
														css={{
															padding: 10,
														}}
													>
														<Typography color={text}>
															{displayDate}
														</Typography>
													</FlexItem>
												</DateCell>
											</TableRow>
										);
									})}
							</>
						</TableBody>
					</Table>
				</Flex>

				{/**
				 * TODO implement reduce function to be able to display multiple items’ dates
				 */}
				{rows[0] !== undefined && (
					<Flex
						direction="column"
						style={datePickerStyle(theme, false)}
						css={{ padding: 20 }}
					>
						<Typography weight="bold" size="lg" css={{ marginBottom: 24 }}>
							{content.specifyFutureDate}
						</Typography>
						<Typography css={{ marginBottom: showError ? 0 : 10 }} color={darkGray}>
							{content.selectADate}
						</Typography>
						<Flex
							css={{
								marginBottom: showError ? 0 : 10,
							}}
						>
							{showError && (
								<Alert severity={"error"}>
									<Typography color={darkGray} size={"sm"}>
										{errorMessage}
									</Typography>
								</Alert>
							)}
						</Flex>

						<div ref={triggerRef as RefObject<HTMLDivElement>}>
							<Flex direction="column">
								<Typography color={darkGray} css={{ marginBottom: 33 }}>
									<DatePickerPopper
										isFutureDates={true}
										id={rows[0].id}
										isFetching={isFetching}
										rowData={rows[0]}
										isMobile={true}
										leadTimeAndDeliveryDates={filteredDeliveryTimes}
										handleOnDeliveryDateChange={handleChange}
										popperDisplayedState={popperDisplayedState}
										externalRef={triggerRef}
									/>
								</Typography>
							</Flex>
						</div>
						<Button
							size="md"
							fullwidth
							label={content.apply}
							disabled={
								!filteredDeliveryTimes ||
								filteredDeliveryTimes.leadTimeDays.length === 0
							}
							onClick={function (
								e:
									| React.MouseEvent<Element, MouseEvent>
									| React.KeyboardEvent<Element>
							): void {
								handleApply();
							}}
						/>
						<Flex
							direction="row"
							justifyContent="flex-start"
							css={{ gap: 10, marginTop: 30, marginBottom: 125 }}
							alignItems="center"
							onClick={function (
								e:
									| React.MouseEvent<Element, MouseEvent>
									| React.KeyboardEvent<Element>
							): void {
								handleRemove();
							}}
						>
							<Icon icon={"trash"} color={link} size="sm" />
							<Typography color={link}>{content.remove}</Typography>
						</Flex>
					</Flex>
				)}
			</SliderMenu>
		</>
	);
};
